import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import { API_URL } from '../Constant';
import { AUTH_USE_HTTPONLY_COOKIE } from '../config/authFlags';
import { getAuthHeaders, getToken, setUserClaimsSnapshot } from './authStorage';

/** UPDES-style claim object (jwt `id` or `/api/auth/me` user). */
function looksLikeUpdesClaims(obj) {
  if (!obj || typeof obj !== 'object') return false;
  return (
    obj.ROLE_CODE != null ||
    obj.USER_ID != null ||
    obj.USER_TYPE != null ||
    obj.USER_NAME != null
  );
}

/**
 * Normalise **`/api/auth/me`** (and similar) payloads → same shape as `jwtDecode`: `{ id: { … }, exp?: number }`.
 */
export function normalizeAuthMePayload(data) {
  if (!data || typeof data !== 'object') return null;

  if (data.id && typeof data.id === 'object') {
    return { id: data.id, exp: data.exp };
  }

  if (data.user != null && typeof data.user === 'object') {
    return { id: data.user, exp: data.exp };
  }

  if (looksLikeUpdesClaims(data)) {
    return { id: data, exp: data.exp };
  }

  const inner = data.profile ?? data.claims ?? data.payload;
  if (inner != null && typeof inner === 'object') {
    if (inner.id && typeof inner.id === 'object') return { id: inner.id, exp: data.exp };
    if (looksLikeUpdesClaims(inner)) return { id: inner, exp: data.exp };
  }

  return null;
}

/**
 * Same as {@link fetchSessionUser} but **`meta`** se pata chale fail kis type ka hai (401 vs 520 vs network).
 */
export async function fetchSessionUserWithMeta() {
  try {
    const res = await axios.get(`${API_URL}api/auth/me`, {
      ...getAuthHeaders(),
      withCredentials: true,
      skipSessionExpiryHandler: true,
      validateStatus: () => true,
    });

    if (res.status === 401 || res.status === 403 || res.status === 404) {
      return { user: null, meta: 'unauthorized' };
    }
    if (res.status >= 500) {
      return { user: null, meta: 'server_error' };
    }
    if (res.status < 200 || res.status >= 300) {
      return { user: null, meta: 'unauthorized' };
    }

    const st = res.data?.status;
    const statusOk =
      st === true ||
      st === 'true' ||
      String(st).toLowerCase() === 'ok' ||
      (st !== false && st !== 'false' && res.data?.data != null);

    if (!statusOk || res.data?.data == null) {
      return { user: null, meta: 'unauthorized' };
    }

    const user = normalizeAuthMePayload(res.data.data);
    if (!user?.id) return { user: null, meta: 'unauthorized' };
    return { user, meta: 'ok' };
  } catch (_) {
    return { user: null, meta: 'network' };
  }
}

export async function fetchSessionUser() {
  const { user } = await fetchSessionUserWithMeta();
  return user;
}

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Server MUST accept current Bearer/cookies — blocks Burp pasted sign-in JSON after logout.
 *
 * @param {string|null} [explicitAccessToken]
 * @returns {Promise<boolean>}
 */
function sessionCheckOk(res) {
  if (res.status >= 500) return { ok: false, meta: 'server_error' };
  if (res.status !== 200) return { ok: false, meta: 'unauthorized' };
  const st = res.data?.status;
  const ok =
    st === true || st === 'true' || String(st).toLowerCase() === 'ok';
  return ok ? { ok: true, meta: 'ok' } : { ok: false, meta: 'unauthorized' };
}

async function requestSessionCheck(extraHeaders = {}) {
  const base = getAuthHeaders();
  const headers = { ...(base.headers || {}), ...extraHeaders };
  if (!headers.Authorization) {
    delete headers.Authorization;
  }
  return axios.get(`${API_URL}api/auth/session-check`, {
    ...base,
    withCredentials: true,
    skipSessionExpiryHandler: true,
    validateStatus: () => true,
    headers,
  });
}

/**
 * Live server: Cloudflare sometimes returns 520 on GET + large Bearer header.
 * Try HttpOnly cookie first (same as page reload), then Bearer.
 *
 * @returns {{ ok: boolean, meta: 'ok'|'unauthorized'|'server_error'|'network' }}
 */
export async function verifyServerSession(explicitAccessToken = null) {
  try {
    const raw =
      (typeof explicitAccessToken === 'string' && explicitAccessToken.trim()) ||
      getToken() ||
      null;
    const bearer = raw
      ? String(raw)
          .trim()
          .replace(/^Bearer\s+/i, '')
          .replace(/^['"]|['"]$/g, '')
      : null;

    const modes = [];
    if (AUTH_USE_HTTPONLY_COOKIE) {
      modes.push('cookie');
    }
    if (bearer) {
      modes.push('bearer');
    }
    if (!modes.length) {
      return { ok: false, meta: 'unauthorized' };
    }

    let lastMeta = 'unauthorized';
    for (const mode of modes) {
      const res = await requestSessionCheck(
        mode === 'bearer' ? { Authorization: `Bearer ${bearer}` } : {}
      );
      const result = sessionCheckOk(res);
      if (result.ok) {
        return result;
      }
      lastMeta = result.meta;
    }
    return { ok: false, meta: lastMeta };
  } catch (_) {
    return { ok: false, meta: 'network' };
  }
}

/**
 * After sign-in: prefer live `session-check` + `/me`. If session-check 520 (Cloudflare) but signin
 * just succeeded, fall back like a page reload — cookies + `/me` or trusted signin body.
 *
 * @param {object|null} signInDataBlock — `data` from signin 200 (used only when transport fails)
 * @param {string|null} [accessToken]
 */
export async function resolveUserAfterSignin(signInDataBlock, accessToken = null) {
  const token =
    (typeof accessToken === 'string' && accessToken.trim()) || getToken() || null;

  if (!token && !AUTH_USE_HTTPONLY_COOKIE) {
    return { user: null, meta: 'unauthorized' };
  }

  let sessionOk = false;
  let sessionMeta = 'unauthorized';
  for (let attempt = 0; attempt < 3; attempt++) {
    const check = await verifyServerSession(token);
    if (check.ok) {
      sessionOk = true;
      break;
    }
    sessionMeta = check.meta || 'unauthorized';
    if (attempt < 2) {
      await sleep(250 * (attempt + 1));
    }
  }

  if (!sessionOk) {
    const transportFail =
      sessionMeta === 'server_error' || sessionMeta === 'network';

    if (transportFail) {
      const { user: meUser } = await fetchSessionUserWithMeta();
      if (meUser?.id) {
        return { user: meUser, meta: 'ok' };
      }

      const fromSignin = signInDataBlock?.user
        ? normalizeAuthMePayload({ user: signInDataBlock.user })
        : normalizeAuthMePayload(signInDataBlock);
      if (fromSignin?.id) {
        if (AUTH_USE_HTTPONLY_COOKIE) {
          setUserClaimsSnapshot(fromSignin);
        }
        return { user: fromSignin, meta: 'signin_ok' };
      }

      if (token) {
        try {
          const decoded = jwtDecode(token);
          const userInfo = shapeDecodedJwtPayload(decoded);
          if (userInfo?.id) {
            if (AUTH_USE_HTTPONLY_COOKIE) {
              setUserClaimsSnapshot(userInfo);
            }
            return { user: userInfo, meta: 'signin_ok' };
          }
        } catch {
          /* ignore */
        }
      }
    }

    return { user: null, meta: sessionMeta };
  }

  for (let attempt = 0; attempt < 2; attempt++) {
    const { user, meta } = await fetchSessionUserWithMeta();
    if (user?.id) {
      return { user, meta: 'ok' };
    }
    if (meta === 'server_error' || meta === 'network') {
      if (attempt === 0) {
        await sleep(400);
        continue;
      }
      try {
        const decoded = jwtDecode(token);
        const userInfo = shapeDecodedJwtPayload(decoded);
        if (userInfo?.id) {
          return { user: userInfo, meta: 'signin_ok' };
        }
      } catch {
        /* ignore */
      }
      return { user: null, meta };
    }
    return { user: null, meta: 'unauthorized' };
  }

  return { user: null, meta: 'unauthorized' };
}

export function normalizeUserFromApiData(data) {
  return normalizeAuthMePayload(data);
}

export function extractAccessTokenFromPayload(payload) {
  if (!payload || typeof payload !== 'object') return null;
  const raw =
    payload.token ??
    payload.access_token ??
    payload.accessToken ??
    payload.jwt ??
    payload.access ??
    payload.id_token;
  if (typeof raw !== 'string' || !raw.trim()) return null;
  return raw
    .trim()
    .replace(/^Bearer\s+/i, '')
    .replace(/^["']|["']$/g, '');
}

export function shapeDecodedJwtPayload(decoded) {
  if (!decoded || typeof decoded !== 'object') return null;
  if (decoded.id && typeof decoded.id === 'object') return decoded;
  const looksLikeUpdesClaimsRoot =
    decoded.STATUS_FLAG != null ||
    decoded.USER_ID != null ||
    decoded.USER_NAME != null ||
    decoded.ROLE_CODE != null ||
    decoded.USER_TYPE != null;
  if (!looksLikeUpdesClaimsRoot) return null;
  const { exp, iat, nbf, aud, iss, sub, ...rest } = decoded;
  return { id: rest, exp, iat, nbf, aud, iss, sub };
}
